import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../auth/AuthContext'
import { llmApi } from '../api/llm'
import { queryKeys } from '../api/queryKeys'
import { AppShell } from '../components/AppShell'
import { AtRiskWidget } from '../components/dashboard/AtRiskWidget'
import { OnboardingChecklist } from '../components/dashboard/OnboardingChecklist'
import { useEntityList } from '../api/hooks/useEntity'
import { ENTITY } from '../types/entities'
import type { Attendance, Grade, SchoolClass, Student } from '../types/entities'
import { localIsoDate } from '../utils/dates'

export function DashboardPage() {
  const { t } = useTranslation()
  const { teacher } = useAuth()
  const usage = useQuery({
    queryKey: queryKeys.llm.usage(),
    queryFn: llmApi.usage,
    retry: false,
  })
  const classes = useEntityList<SchoolClass>(ENTITY.schoolClass)
  const students = useEntityList<Student>(ENTITY.student)
  const grades = useEntityList<Grade>(ENTITY.grade)
  const attendance = useEntityList<Attendance>(ENTITY.attendance)

  if (!teacher) return null

  const activeClasses = (classes.data ?? []).filter((c) => c.archivedAt == null)
  const activeStudents = (students.data ?? [])

  const today = localIsoDate()
  const gradesToday = (grades.data ?? []).filter((g) => g.date === today).length
  const attendanceToday = (attendance.data ?? []).filter((a) => a.date === today).length

  return (
    <AppShell>
      <div className="space-y-6">
        {/* Onboarding (скрывается когда все шаги выполнены или явно закрыт) */}
        <OnboardingChecklist />

        {/* Quick-nav tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <NavTile to="/app/classes"    icon="📘" title={t('dashboardPage.navClasses')}       hint={t('dashboardPage.navClassesHint', { count: activeClasses.length })} />
          <NavTile to="/app/students"   icon="👥" title={t('dashboardPage.navStudents')}      hint={t('dashboardPage.navStudentsHint', { count: activeStudents.length })} />
          <NavTile to="/app/journal"    icon="📊" title={t('dashboardPage.navJournal')}       hint={gradesToday > 0 ? t('dashboardPage.navJournalHint', { count: gradesToday }) : t('dashboardPage.navJournalEmpty')} />
          <NavTile to="/app/attendance" icon="✅" title={t('dashboardPage.navAttendance')} hint={attendanceToday > 0 ? t('dashboardPage.navAttendanceHint', { count: attendanceToday }) : t('dashboardPage.navAttendanceEmpty')} />
        </div>

        {/* Push-аналитика: группа риска (Фаза 4) */}
        <AtRiskWidget />

        {/* LLM-usage card */}
        <div className="grid grid-cols-3 gap-2 sm:gap-4">
          <StatCard
            label={t('dashboardPage.statRequestsToday')}
            value={usage.isLoading ? '…' : `${usage.data?.countToday ?? 0} / ${usage.data?.limitPerDay ?? '∞'}`}
            color="bg-blue-50 text-blue-700"
          />
          <StatCard
            label={t('dashboardPage.statMonth')}
            value={usage.isLoading ? '…' : (usage.data?.countMonth ?? 0).toString()}
            color="bg-green-50 text-green-700"
          />
          <StatCard
            label={t('dashboardPage.statRemainingToday')}
            value={usage.isLoading ? '…' : (usage.data?.remainingToday ?? 0).toString()}
            color="bg-purple-50 text-purple-700"
          />
        </div>

        {/* Tools */}
        <h2 className="text-lg font-semibold mb-3">{t('dashboardPage.toolsHeading')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <ToolCard
            to="/app/tools/lesson-plan"
            title={t('dashboardPage.toolLessonPlanTitle')}
            description={t('dashboardPage.toolLessonPlanDesc')}
            badge={t('dashboardPage.badgeAi')}
          />
          <ToolCard
            to="/app/tools/quiz"
            title={t('dashboardPage.toolQuizTitle')}
            description={t('dashboardPage.toolQuizDesc')}
            badge={t('dashboardPage.badgeAi')}
          />
          <ToolCard
            to="/app/tools/text-leveler"
            title={t('dashboardPage.toolTextLevelerTitle')}
            description={t('dashboardPage.toolTextLevelerDesc')}
            badge={t('dashboardPage.badgeAi')}
          />
          <ToolCard
            to="/app/tools/rubric"
            title={t('dashboardPage.toolRubricTitle')}
            description={t('dashboardPage.toolRubricDesc')}
            badge={t('dashboardPage.badgeAi')}
          />
          <ToolCard
            to="/app/tools/characteristic"
            title={t('dashboardPage.toolCharacteristicTitle')}
            description={t('dashboardPage.toolCharacteristicDesc')}
            badge={t('dashboardPage.badgeAi')}
          />
          <ToolCard
            to="/app/tools/parent-letter"
            title={t('dashboardPage.toolParentLetterTitle')}
            description={t('dashboardPage.toolParentLetterDesc')}
            badge={t('dashboardPage.badgeAi')}
          />
          <ToolCard
            to="/app/tools/worksheet"
            title={t('dashboardPage.toolWorksheetTitle')}
            description={t('dashboardPage.toolWorksheetDesc')}
            badge={t('dashboardPage.badgeAi')}
          />
          <ToolCard
            to="/app/tools/self-education"
            title={t('dashboardPage.toolSelfEducationTitle')}
            description={t('dashboardPage.toolSelfEducationDesc')}
            badge={t('dashboardPage.badgeAi')}
          />
          <ToolCard
            to="/app/tools/class-portrait"
            title={t('dashboardPage.toolClassPortraitTitle')}
            description={t('dashboardPage.toolClassPortraitDesc')}
            badge={t('dashboardPage.badgeAi')}
          />
          <ToolCard
            to="/app/tools/quarter-report"
            title={t('dashboardPage.toolQuarterReportTitle')}
            description={t('dashboardPage.toolQuarterReportDesc')}
            badge={t('dashboardPage.badgeAi')}
          />
          <ToolCard
            to="/app/tools/topic-analysis"
            title={t('dashboardPage.toolTopicAnalysisTitle')}
            description={t('dashboardPage.toolTopicAnalysisDesc')}
            badge={t('dashboardPage.badgeAi')}
          />
          <ToolCard
            to="/app/tools/vision"
            title={t('dashboardPage.toolVisionTitle')}
            description={t('dashboardPage.toolVisionDesc')}
            badge={t('dashboardPage.badgeDemo')}
          />
          <ToolCard
            to="/app/tools/essay-check"
            title={t('dashboardPage.toolEssayCheckTitle')}
            description={t('dashboardPage.toolEssayCheckDesc')}
            badge={t('dashboardPage.badgeAi')}
          />
          <ToolCard
            to="/app/tools/notebook"
            title={t('dashboardPage.toolNotebookTitle')}
            description={t('dashboardPage.toolNotebookDesc')}
            badge={t('dashboardPage.badgeAi')}
          />
        </div>

        <div className="mt-12 p-4 bg-green-50 border border-green-200 rounded text-sm">
          <strong>{t('dashboardPage.fullFunctionalityTitle')}</strong> {t('dashboardPage.fullFunctionalityText')}
        </div>
      </div>
    </AppShell>
  )
}

function NavTile({ to, icon, title, hint }: { to: string; icon: string; title: string; hint?: string }) {
  return (
    <Link
      to={to}
      className="group bg-white border border-gray-200 rounded-xl p-3 sm:p-4 flex items-center gap-2 sm:gap-3 transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-[0_8px_24px_-8px_rgba(87,10,166,0.2)]"
    >
      <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-gradient-to-br from-primary-soft to-purple-100 flex items-center justify-center text-xl sm:text-2xl group-hover:scale-110 transition-transform flex-shrink-0">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-sm sm:text-base text-gray-900 group-hover:text-primary leading-tight">{title}</div>
        {hint && <div className="text-xs text-gray-500 leading-tight mt-0.5 line-clamp-2">{hint}</div>}
      </div>
    </Link>
  )
}

function StatCard({
  label,
  value,
  color,
}: {
  label: string
  value: string
  color: string
}) {
  return (
    <div className={`${color} rounded-lg p-3 sm:p-4`}>
      <div className="text-[10px] sm:text-xs font-medium opacity-75 leading-tight">{label}</div>
      <div className="text-lg sm:text-2xl font-bold mt-1 truncate">{value}</div>
    </div>
  )
}

function ToolCard({
  to,
  title,
  description,
  badge,
  disabled,
}: {
  to: string
  title: string
  description: string
  badge?: string
  disabled?: boolean
}) {
  const className = `bg-white border border-gray-200 rounded-lg p-4 transition-all ${
    disabled
      ? 'opacity-50 cursor-not-allowed'
      : 'hover:border-primary hover:shadow-md cursor-pointer'
  }`
  const content = (
    <div className={className}>
      <div className="flex items-start justify-between mb-2">
        <h3 className="font-semibold">{title}</h3>
        {badge && (
          <span
            className={`text-xs px-2 py-0.5 rounded ${
              disabled ? 'bg-gray-100 text-gray-500' : 'bg-primary-soft text-primary'
            }`}
          >
            {badge}
          </span>
        )}
      </div>
      <p className="text-sm text-gray-600">{description}</p>
    </div>
  )
  return disabled ? content : <Link to={to}>{content}</Link>
}
